import {useQueryClient} from "@tanstack/react-query";
import {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";

import useInvalidateSession from "@/src/modules/auth/hooks/use-invalidate-session";
import {PatrolVehicleConditionDialog} from "@/src/modules/company-vehicles/components";
import {
  isPatrolConditionSheetId,
  patrolConditionModeFromSheetId,
} from "@/src/modules/company-vehicles/lib/patrol-condition-form-draft";
import {shouldPromptPatrolStartConditionCheck} from "@/src/modules/company-vehicles/lib/patrol-vehicle-condition";
import {getCurrentShiftCompanyVehicle} from "@/src/modules/company-vehicles/lib/shift-company-vehicles";
import type {FormDraftRecord} from "@/src/modules/core/lib/form-draft";
import {dispatchCenterRequiresShift, isDispatchOperatorRole} from "@/src/modules/core/lib/dispatch-center-policy";
import {useToast} from "@/src/modules/core/hooks";
import {resolveShiftGpsForSubmit} from "@/src/modules/core/lib/resolve-shift-gps-coordinates";
import {useFormDraftResumeStore} from "@/src/modules/core/store/form-draft-resume";
import {useStoredMe} from "@/src/modules/core/store/me";
import type {Shift} from "@/src/modules/shifts/types";

import {
  ActiveShift,
  BreakCard,
  EmptyShift,
  NextShift,
  PatrolCheckpointList,
  QuickActions,
  UpcomingShifts,
} from "../components";
import {HomeSkeleton} from "../components/home-skeleton";
import {useActiveShift, useScheduledShifts, useShiftEnd} from "../hooks";
import {showPatrolCheckpoints} from "../lib/show-patrol-checkpoints";
import {getShiftBreaks} from "@/src/modules/shifts/lib/shift-breaks";
import {PageHeader} from "../../core/components/page-header";

function parsePatrolShiftIdFromDraft(draft: FormDraftRecord): number | null {
  if (draft.entityId != null) {
    const id = Number(draft.entityId);
    return Number.isFinite(id) ? id : null;
  }
  const match = draft.draftKey.match(/shift(\d+)$/);
  return match ? Number(match[1]) : null;
}

export default function OfficerHome() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const invalidateSession = useInvalidateSession();
  const toast = useToast();
  const me = useStoredMe();
  const requiresShift = dispatchCenterRequiresShift(me);
  const isDispatchOperator = isDispatchOperatorRole(me);
  const showFullQuickActions = !isDispatchOperator;
  const {mutateAsync: shiftEnd} = useShiftEnd();

  const {shift: activeShift, isSuccess: activeShiftQuerySucceeded} = useActiveShift();
  const {isPending: isPendingScheduledShifts, nextShift, upcomingShifts} = useScheduledShifts();

  const showEmergencyShiftWhenOffShift =
    isDispatchOperator && requiresShift && !activeShift;
  const showQuickActionsColumn = showFullQuickActions || showEmergencyShiftWhenOffShift;

  const pendingDraft = useFormDraftResumeStore((state) => state.pending);
  const consumePending = useFormDraftResumeStore((state) => state.consumePending);
  const resumeHandledRef = useRef(false);

  const [patrolResume, setPatrolResume] = useState<{
    mode: "start" | "end";
    shiftId: number;
    requestEndShift: boolean;
    lockedVehicle?: {id: number; name?: string | null} | null;
  } | null>(null);
  const [draftRestoreValues, setDraftRestoreValues] = useState<Record<string, unknown> | null>(
    null,
  );

  useEffect(() => {
    if (resumeHandledRef.current || !pendingDraft || !isPatrolConditionSheetId(pendingDraft.sheetId)) {
      return;
    }

    const mode = patrolConditionModeFromSheetId(pendingDraft.sheetId);
    if (!mode) {
      return;
    }

    const shiftId = parsePatrolShiftIdFromDraft(pendingDraft);
    if (shiftId == null) {
      return;
    }

    if (!activeShiftQuerySucceeded) {
      return;
    }

    if (!activeShift || activeShift.shift_id !== shiftId) {
      resumeHandledRef.current = true;
      consumePending();
      return;
    }

    resumeHandledRef.current = true;
    const draft = consumePending();
    if (!draft) {
      return;
    }

    setDraftRestoreValues(draft.values);
    setPatrolResume({
      mode,
      shiftId,
      requestEndShift: mode === "end",
      lockedVehicle: mode === "end" ? getCurrentShiftCompanyVehicle(activeShift) : null,
    });
  }, [activeShift, activeShiftQuerySucceeded, consumePending, pendingDraft]);

  const openPatrolStartCheck =
    patrolResume == null &&
    activeShiftQuerySucceeded &&
    activeShift != null &&
    shouldPromptPatrolStartConditionCheck(activeShift);

  const showPatrolDialog =
    (patrolResume != null || openPatrolStartCheck) && activeShift != null;

  const patrolMode = patrolResume?.mode ?? "start";
  const patrolShiftId = patrolResume?.shiftId ?? activeShift?.shift_id ?? 0;

  const handlePatrolDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setPatrolResume(null);
      setDraftRestoreValues(null);
    }
  }, []);

  const handlePatrolComplete = useCallback(async () => {
    await queryClient.refetchQueries({queryKey: ["active-shift"]});
    setPatrolResume(null);
    setDraftRestoreValues(null);
    if (isDispatchOperatorRole(me) && dispatchCenterRequiresShift(me)) {
      navigate("/dispatchdesk", {replace: true});
    }
  }, [me, navigate, queryClient]);

  const handleRequestEndShift = useCallback(async () => {
    if (!patrolResume) {
      return;
    }
    const shiftForGps =
      queryClient.getQueryData<{shift: Shift | null}>(["active-shift"])?.shift ?? activeShift;
    const gps = await resolveShiftGpsForSubmit(shiftForGps, "shift_end");
    if (!gps) {
      return;
    }
    await shiftEnd({
      shift_id: patrolResume.shiftId,
      gps_coordinates: gps.gps_coordinates,
      gps_source: gps.gps_source,
    });
  }, [activeShift, patrolResume, queryClient, shiftEnd]);

  const handleEndPatrolComplete = useCallback(async () => {
    toast.success("Shift ended successfully");
    setPatrolResume(null);
    setDraftRestoreValues(null);
    invalidateSession();
    navigate("/login", {replace: true});
  }, [invalidateSession, navigate, toast]);

  if (requiresShift && isPendingScheduledShifts) {
    return <HomeSkeleton showQuickActions={showQuickActionsColumn} />;
  }

  const patrolCheckpointsVisible =
    activeShift != null && showPatrolCheckpoints(activeShift);

  return (
    <>
      {showPatrolDialog ? (
        <PatrolVehicleConditionDialog
          key={`patrol-${patrolMode}-${patrolShiftId}-${patrolResume ? "resume" : "forced"}`}
          open
          onOpenChange={patrolResume ? handlePatrolDialogClose : () => {}}
          mode={patrolMode}
          shiftId={patrolShiftId}
          lockedVehicle={patrolResume?.lockedVehicle ?? null}
          onRequestEndShift={patrolResume?.requestEndShift ? handleRequestEndShift : undefined}
          onComplete={
            patrolResume?.mode === "end" && patrolResume.requestEndShift
              ? handleEndPatrolComplete
              : handlePatrolComplete
          }
          draftRestoreValues={patrolResume ? draftRestoreValues : null}
          onDraftRestored={() => setDraftRestoreValues(null)}
        />
      ) : null}
      <PageHeader>
        <PageHeader.TitleWrapper>
          <PageHeader.Title title="Home" />
        </PageHeader.TitleWrapper>
      </PageHeader>

      <div className="p-4 md:p-6">
        <div className="flex h-full flex-col gap-4">
          {requiresShift && activeShift && getShiftBreaks(activeShift).length > 0 ? (
            <BreakCard shift={activeShift} />
          ) : null}
          {/*
            Desktop: Active Shift | Quick Actions (equal height), then Checkpoints full width.
            Mobile: Active → Checkpoints → Quick Actions (QA last via auto-placement / DOM order).
          */}
          <div className="grid gap-4 lg:grid-cols-3 lg:items-stretch">
            {requiresShift ? (
              <div
                className={
                  showQuickActionsColumn
                    ? "flex h-full min-h-0 flex-1 flex-col lg:col-span-2 lg:min-h-[18rem]"
                    : "flex h-full min-h-0 flex-1 flex-col lg:col-span-3 lg:min-h-[18rem]"
                }
              >
                {activeShift ? <ActiveShift shift={activeShift} /> : null}
                {nextShift ? <NextShift shift={nextShift} /> : null}
                {!activeShift && !nextShift ? <EmptyShift /> : null}
              </div>
            ) : null}

            {patrolCheckpointsVisible && activeShift ? (
              <div className="min-w-0 lg:col-span-3">
                <PatrolCheckpointList
                  shiftId={activeShift.shift_id}
                  visits={activeShift.patrol_visits ?? []}
                />
              </div>
            ) : null}

            {showQuickActionsColumn ? (
              <div
                className={
                  requiresShift
                    ? "flex h-full min-h-0 flex-col lg:col-start-3 lg:row-start-1 lg:min-h-[18rem]"
                    : "flex h-full min-h-0 flex-col lg:col-span-3 lg:min-h-[18rem]"
                }
              >
                <QuickActions
                  key={activeShift?.shift_id ?? "no-active-shift"}
                  isOnActiveShift={Boolean(activeShift)}
                  requireActiveShift={requiresShift}
                  emergencyOnly={showEmergencyShiftWhenOffShift}
                />
              </div>
            ) : null}
          </div>

          {requiresShift && (nextShift || activeShift) ? (
            <div className="lg:min-h-[24rem]">
              <UpcomingShifts
                upcomingShifts={upcomingShifts}
                listScope={activeShift ? "full" : "after-next"}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
